import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { editClients } from '../../redux/actions/clients';
import ErrorMsgField from '../../components/ErrorMsgField';
/* components */
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import ModalContactsHistory from '../../components/ModalContactsHistory';
/* form-validation */
import schema from '../../form-validation/client';
/* utils */
import handleChangeDefault from '../../utils/handleChangeDefault';
import api from '../../api';
import ptBR from 'date-fns/locale/pt-BR';
registerLocale('ptBR', ptBR);

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    name: '',
    socialName: '',
    type: '1',
    gender: '',
    cpf: '',
    rg: '',
    cnpj: '',
    ie: '',
    born: new Date(),
    email: '',
    phones: {
      main: '',
      message: ''
    },
    address: {
      street: '',
      number: '',
      zipCode: '',
      complement: ''
    },
    allowNotifications: false,
    additionalInformation: '',
    facebook: '',
    instagram: '',
    contactsHistory: [],
    company: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, setError } = useForm({
    validationSchema: schema,
    reValidateMode: 'onBlur'
  });
  const clients = useSelector(state => state.clients);
  const [data, setData] = useState(INITIAL_VALUES);
  const [requestZipCodeError, setRequestZipCodeError] = useState(false);
  const regexZipCode = /^\d{5}-\d{3}$/;
  const [showModalContactsHistory, setShowModalContactsHistory] = useState(
    false
  );

  useEffect(() => {
    (async function () {
      try {
        const params = { _id: id };
        const { data } = await api().get('/api/clients', { params });
        if (data[0]) {
          const newData = {
            ...INITIAL_VALUES,
            ...data[0],
            type: String(data[0].type),
            born: new Date(data[0].born)
          };
          setData(newData);
        }
      } catch (err) {
        console.error('Error: ', err);
      }
    })();
  }, []);

  const submit = () => {
    const newData = {
      ...data,
      cnpj: parseInt(data.type) === 2 ? data.cnpj : undefined,
      ie: parseInt(data.type) === 2 ? data.ie : undefined,
      gender: parseInt(data.type) === 1 ? data.gender : undefined,
      rg: parseInt(data.type) === 1 ? data.rg : undefined,
      cpf: parseInt(data.type) === 1 ? data.cpf : undefined
    };
    dispatch(editClients(newData, submitButton.current));
  };

  const handleChangePhones = evt => {
    const { name, value } = evt.target;
    setData({ ...data, phones: { ...data.phones, [name]: value } });
  };

  const handleBlurPhone = evt => {
    const { name, value } = evt.target;
    if (/^\(\d{2}\) \d{4,5}\d{4}$/.test(value.trim())) {
      const splited = value.split(' ');
      const number = `${splited[0]} ${splited[1].slice(
        0,
        -4
      )}-${splited[1].slice(-4)}`;
      evt.target.value = number;
      setData({ ...data, phones: { ...data.phones, [name]: number } });
      setError(name);
    }
  };

  const handleChangeAddress = evt => {
    const { name, value } = evt.target;
    if (evt.target.getAttribute('number') === 'on') {
      if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
        setData({ ...data, address: { ...data.address, [name]: value } });
      } else {
        evt.target.value = data.address[evt.target.name];
      }
    } else {
      setData({ ...data, address: { ...data.address, [name]: value } });
    }
  };

  const handleChangeZipCode = evt => {
    const { name, value } = evt.target;
    setData({ ...data, address: { ...data.address, [name]: value } });
    if (regexZipCode.test(value)) {
      api()
        .get(`/api/viacep/${value}`)
        .then(response => {
          setRequestZipCodeError(false);
          const { logradouro, bairro, localidade, uf, cep } = response.data;
          if (logradouro && bairro && localidade && uf) {
            setData({
              ...data,
              address: {
                ...data.address,
                street: `${logradouro} - ${bairro}, ${localidade} - ${uf}`,
                zipCode: cep
              }
            });
            setError(name);
          } else {
            setError(name, 'zipCode', 'Invalid ZipCode');
          }
        })
        .catch(err => {
          setRequestZipCodeError(true);
          console.error('Error: ', err);
        });
    }
  };

  const handleChangeCPF = evt => {
    const { name, value } = evt.target;
    setData({ ...data, [name]: value });
    if (/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)) {
      setError(name);
    }
  };

  const handleChangeRG = evt => {
    const { name, value } = evt.target;
    setData({ ...data, [name]: value });
  };

  const handleChangeCNPJ = evt => {
    const { name, value } = evt.target;
    setData({ ...data, [name]: value });
    if (/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value)) {
      setError(name);
    }
  };

  const handleChangeCheckOption = evt => {
    const { checked, name } = evt.target;
    setData({ ...data, [name]: checked });
  };

  return (
    <div className="container-fluid">
      <ModalContactsHistory
        showModal={showModalContactsHistory}
        handleModalClose={() => setShowModalContactsHistory(false)}
        client={id}
      />
      {clients.editSuccess && (
        <AlertMsg type="success" msg="Cliente atualizado com sucesso" />
      )}
      {clients.errorContent &&
        !clients.errorContent.message.includes('400') &&
        clients.editError && (
        <AlertMsg type="danger" msg="Falha durante atualização do cliente" />
      )}
      {clients.errorContent &&
        clients.errorContent.message.includes('400') &&
        clients.editError && (
        <AlertMsg
          type="danger"
          msg="Um cliente com o cpf informado já está cadastrado"
        />
      )}
      {!clients.editSuccess && !clients.editError && (
        <AlertMsg type="info" msg="Área atualização de clientes" />
      )}
      <FormHeader title="Atualizar Cliente" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="type">
                Tipo<span className="field-required">*</span>
              </label>
              <select
                id="type"
                name="type"
                className="form-control"
                value={data.type}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                <option value="1">Física</option>
                <option value="2">Jurídica</option>
              </select>
              {errors.type && errors.type.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-5">
            <div className="form-group">
              <label htmlFor="name">
                {data.type === '1' ? 'Nome Completo' : 'Razão Social'}
                <span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="socialName">
                Nome {data.type === '1' ? 'Social' : 'Fantasia'}
                <span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="socialName"
                name="socialName"
                autoComplete="off"
                value={data.socialName}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.socialName && errors.socialName.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              {data.type === '1' ? (
                <React.Fragment>
                  <label htmlFor="cpf">
                    CPF<span className="field-required">*</span>
                  </label>
                  <InputMask
                    id="cpf"
                    name="cpf"
                    mask="999.999.999-99"
                    className="form-control"
                    autoComplete="off"
                    placeholder="___.___.___-__"
                    value={data.cpf}
                    onChange={handleChangeCPF}
                    ref={register}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <label htmlFor="cnpj">
                    CNPJ<span className="field-required">*</span>
                  </label>
                  <InputMask
                    id="cnpj"
                    name="cnpj"
                    mask="99.999.999/9999-99"
                    className="form-control"
                    autoComplete="off"
                    placeholder="__.___.___/____-__"
                    value={data.cnpj}
                    onChange={handleChangeCNPJ}
                    ref={register}
                  />
                </React.Fragment>
              )}
              {errors.cpf && errors.cpf.type === 'cpf' && (
                <ErrorMsgField type="cpf" />
              )}
              {errors.cnpj && errors.cnpj.type === 'cnpj' && (
                <ErrorMsgField type="cnpj" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              {data.type === '1' ? (
                <React.Fragment>
                  <label htmlFor="rg">RG</label>
                  <InputMask
                    id="rg"
                    name="rg"
                    maskChar=""
                    mask="***************"
                    formatChars={{
                      '*': '[0-9]|[xX]'
                    }}
                    className="form-control"
                    autoComplete="off"
                    value={data.rg}
                    onChange={handleChangeRG}
                    beforeMaskedValueChange={evt => {
                      const { value } = evt;
                      evt.value = value.toUpperCase();
                      return evt;
                    }}
                    ref={register}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <label htmlFor="ie">IE</label>
                  <InputMask
                    id="ie"
                    name="ie"
                    maskChar=""
                    mask="999999999999"
                    className="form-control"
                    autoComplete="off"
                    value={data.ie}
                    onChange={evt => handleChangeDefault(evt, data, setData)}
                    ref={register}
                  />
                </React.Fragment>
              )}
              {errors.rg && errors.rg.type === 'rg' && (
                <ErrorMsgField type="rg" />
              )}
              {errors.ie && errors.ie.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        {data.type === '1' && (
          <div className="form-group">
            <label htmlFor="gender">
              Gênero<span className="field-required">*</span>
            </label>
            <select
              id="gender"
              name="gender"
              className="form-control"
              value={data.gender}
              onChange={evt => handleChangeDefault(evt, data, setData)}
              ref={register}
            >
              <option value="">-- Selecione --</option>
              <option value="F">Feminino</option>
              <option value="M">Masculino</option>
              <option value="N">Neutro</option>
              <option value="NR">Não Cadastrado</option>
            </select>
            {errors.gender && errors.gender.type === 'required' && (
              <ErrorMsgField type="required" />
            )}
          </div>
        )}
        <div className="row">
          <div className="col-sm-8">
            <div className="form-group">
              <label htmlFor="email">E-mail</label>
              <input
                type="text"
                className="form-control"
                id="email"
                name="email"
                autoComplete="off"
                value={data.email}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.email && <ErrorMsgField type="email" />}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="born">
                Data {data.type === '1' ? 'Nascimento' : 'Fundação'}
                <span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="born"
                onChange={date => setData({ ...data, born: date })}
                selected={data.born}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.born} />}
              />
              {errors.born && errors.born.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="main">
                Telefone Principal<span className="field-required">*</span>
              </label>
              <InputMask
                id="main"
                name="main"
                maskChar=""
                mask="(99) **********"
                formatChars={{
                  9: '[0-9]',
                  '*': '[0-9]|[-]'
                }}
                className="form-control"
                autoComplete="off"
                value={data.phones.main}
                placeholder="(__) _____-____"
                onChange={handleChangePhones}
                onBlur={handleBlurPhone}
                ref={register}
              />
              {errors.main && errors.main.type === 'phone' && (
                <ErrorMsgField type="phone" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="message">Telefone Recado</label>
              <InputMask
                id="message"
                name="message"
                maskChar=""
                mask="(99) **********"
                formatChars={{
                  9: '[0-9]',
                  '*': '[0-9]|[-]'
                }}
                className="form-control"
                autoComplete="off"
                value={data.phones.message}
                placeholder="(__) _____-____"
                onChange={handleChangePhones}
                onBlur={handleBlurPhone}
                ref={register}
              />
              {errors.message && errors.message.type === 'phone' && (
                <ErrorMsgField type="phone" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="facebook">Facebook</label>
              <input
                type="text"
                className="form-control"
                id="facebook"
                name="facebook"
                autoComplete="off"
                value={data.facebook}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="instagram">Instagram</label>
              <input
                type="text"
                className="form-control"
                id="instagram"
                name="instagram"
                autoComplete="off"
                value={data.instagram}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="zipCode">
                CEP<span className="field-required">*</span>
              </label>
              <InputMask
                id="zipCode"
                name="zipCode"
                mask="99999-999"
                className="form-control"
                autoComplete="off"
                placeholder="_____-___"
                value={data.address.zipCode}
                onChange={handleChangeZipCode}
                ref={register}
              />
              {errors.zipCode && errors.zipCode.type === 'zipCode' && (
                <ErrorMsgField type="zipCode" />
              )}
            </div>
          </div>
          <div className="col-sm-7">
            <div className="form-group">
              <label htmlFor="street">
                Logradouro<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="street"
                name="street"
                autoComplete="off"
                value={data.address.street}
                disabled={!requestZipCodeError}
                onChange={handleChangeAddress}
                ref={register}
              />
              {errors.street && errors.street.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="number">
                Número<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="number"
                name="number"
                autoComplete="off"
                number="on"
                onChange={handleChangeAddress}
                value={data.address.number}
                disabled={!regexZipCode.test(data.address.zipCode)}
                ref={register}
              />
              {errors.number && errors.number.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="complement">Complemento</label>
              <input
                type="text"
                className="form-control"
                id="complement"
                name="complement"
                autoComplete="off"
                value={data.address.complement}
                onChange={handleChangeAddress}
                ref={register}
              />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="allowNotifications">Notificações</label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="allowNotifications"
                  checked={data.allowNotifications}
                  onChange={handleChangeCheckOption}
                ></Switch>
                <span>
                  &nbsp;&nbsp;Aceito receber notificações sobre produtos e
                  serviços
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="contactsHistory">
            Últimas Informações&nbsp;
            <button
              type="button"
              disabled={
                showModalContactsHistory || data.contactsHistory.length <= 0
              }
              className="btn btn-dark btn-sm"
              onClick={() => setShowModalContactsHistory(true)}
            >
              Mostrar Todos
            </button>
          </label>
          {data.contactsHistory.length <= 0 && (
            <p>Nenhuma informação adicional até o momento</p>
          )}
          {data.contactsHistory.map(contact => {
            return (
              <p key={contact._id}>
                {new Date(contact.date).toLocaleString()} - {contact.user.name}:{' '}
                {contact.content}
              </p>
            );
          })}
        </div>
        <div className="form-group">
          <label htmlFor="additionalInformation">Informações Adicionais</label>
          <textarea
            className="form-control"
            id="additionalInformation"
            name="additionalInformation"
            rows="3"
            value={data.additionalInformation}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
        </div>
        <FormFooter url="/clients" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
